import { Container, FederatedPointerEvent, Graphics } from 'pixi.js';
import { MonoBehavior } from 'game/core/monoBehavior';
import { getScreenSize } from 'game/core/screen';
import { Time } from 'game/core/pixiRenderer';
import { lerp } from 'game/core/lerp';
import { Layout } from 'game/LayoutManager';
import { DESKTOP_UI_WIDTH, Displays } from 'constants/layout';

export class ScrollContainer extends MonoBehavior {
    public container: Container;
    public mask: Graphics;
    public prevContainer: Container;
    private mouseDown: boolean;
    public currentPageY: number;

    private scrollValue = 0;
    private scrollbarValue = 0;
    private maxScrollHeight = 1;
    private maxScrollbarHeight = 1;
    private startScroll = 0;
    private startPageY = 0;
    public mainScroll: Graphics;
    public indicatorScroll: Graphics;
    private smoothScrollActive = false;

    pointerScrollArea: Container;
    scrollArea: Graphics;
    handlerDown = false;

    constructor() {
        super();
        this.wheelHandler = this.wheelHandler.bind(this);
        this.onPointerDown = this.onPointerDown.bind(this);
        this.onPointerUp = this.onPointerUp.bind(this);
        this.onPointerMove = this.onPointerMove.bind(this);
        this.magnifyHandler = this.magnifyHandler.bind(this);

        this.onPointerDownHandler = this.onPointerDownHandler.bind(this);
        this.onPointerUpHandler = this.onPointerUpHandler.bind(this);

        this.container = new Container();
        this.pointerScrollArea = new Container();
        this.prevContainer = new Container();
        this.mainScroll = new Graphics();
        this.indicatorScroll = new Graphics();

        this.scrollArea = new Graphics();

        this.pointerScrollArea.addChild(this.scrollArea);

        this.mouseDown = false;
        this.currentPageY = 0;

        this.mask = new Graphics();

        this.container.mask = this.mask;
        this.container.addChild(this.mask);
        this.container.addChild(this.pointerScrollArea);
        this.container.addChild(this.prevContainer);

        this.mainScroll.alpha = 0.05;
        this.indicatorScroll.roundRect(DESKTOP_UI_WIDTH * 0.5 - 24, 30, 4, 96, 2).fill('#FFFFFF');
        this.indicatorScroll.alpha = 0.3;
        this.container.addChild(this.mainScroll);
        this.container.addChild(this.indicatorScroll);

        this.indicatorScroll.position.x = 10;

        this.prevContainer.on('pointerdown', this.onPointerDown);
        this.pointerScrollArea.on('pointerdown', this.onPointerDown);

        this.indicatorScroll.on('pointerdown', this.onPointerDownHandler);
        this.indicatorScroll.on('pointerleave', this.onPointerUpHandler);
        this.indicatorScroll.on('pointerover', this.magnifyHandler);
    }
    magnifyHandler() {
        this.indicatorScroll.clear();
        this.indicatorScroll.roundRect(DESKTOP_UI_WIDTH * 0.5 - 26, 30, 8, 96, 4).fill('#FFFFFF');
    }
    addContent(container: Container) {
        this.prevContainer.addChild(container);
    }
    drawMask() {
        const { height } = getScreenSize();
        const displays = Layout.getDisplay();
        let maskMarginTop = 125;

        switch (displays) {
            case Displays.DESKTOP:
            case Displays.TABLET:
                maskMarginTop = 125;
                break;
            case Displays.MOBILE:
                maskMarginTop = 115;
                break;
        }
        const maskEnd = height - this.container.position.y - maskMarginTop;
        this.mask.clear();
        this.mask.rect(-DESKTOP_UI_WIDTH * 0.5, maskMarginTop, DESKTOP_UI_WIDTH, maskEnd);
        this.mask.fill(0xffffff);
    }
    drawScrollArea() {
        const { height } = getScreenSize();
        const displays = Layout.getDisplay();
        let maskMarginTop = 125;

        switch (displays) {
            case Displays.DESKTOP:
            case Displays.TABLET:
                maskMarginTop = 125;
                break;
            case Displays.MOBILE:
                maskMarginTop = 115;
                break;
        }
        const maskEnd = height - this.container.position.y - maskMarginTop;
        this.scrollArea.clear();
        this.scrollArea.rect(-DESKTOP_UI_WIDTH * 0.5 + 30, maskMarginTop, DESKTOP_UI_WIDTH - 70, maskEnd);
        this.scrollArea.fill(0xffffff);
        this.scrollArea.alpha = 0.000001;
    }

    onPointerMove(e: PointerEvent) {
        if (this.mouseDown) {
            this.smoothScrollActive = true;
            const diff = this.currentPageY - e.pageY;
            this.scrollHandler(diff);
            this.currentPageY = e.pageY;
        }
        if (this.handlerDown) {
            this.smoothScrollActive = false;
            this.scrollHandlerIndicator(e.pageY);
            this.currentPageY = e.pageY;
        }
    }

    onPointerUp(e: PointerEvent) {
        this.mouseDown = false;
        this.handlerDown = false;
        const diffTime = performance.now() - this.startScroll;
        const diffPos = Math.abs(this.currentPageY - this.startPageY);

        this.currentPageY = 0;
        this.onPointerUpHandler();
        if (diffTime > 500) return true;
        if (diffPos > 30) return true;

        return false;
    }
    onPointerDown(e: FederatedPointerEvent) {
        if (this.handlerDown) return;
        this.mouseDown = true;
        this.currentPageY = e.pageY;
        this.startPageY = e.pageY;
        this.startScroll = performance.now();
    }

    onPointerDownHandler(e: FederatedPointerEvent) {
        if (this.mouseDown) return;
        this.handlerDown = true;
        this.currentPageY = e.pageY;
        this.startPageY = e.pageY;
        this.startScroll = performance.now();
    }

    onPointerUpHandler() {
        if (this.handlerDown) return;
        this.indicatorScroll.clear();
        this.indicatorScroll.roundRect(DESKTOP_UI_WIDTH * 0.5 - 24, 30, 4, 96, 2).fill('#FFFFFF');
    }

    setActive(active: boolean) {
        this.container.visible = active;

        this.scrollValue = 0;
        this.scrollbarValue = this.mainScroll.position.y;
        this.prevContainer.position.y = 0;
        if (active) {
            this.scrollArea.eventMode = 'static';
            this.prevContainer.eventMode = 'static';
            this.indicatorScroll.eventMode = 'static';
            this.pointerScrollArea.eventMode = 'static';
            this.indicatorScroll.cursor = 'pointer';

            addEventListener('wheel', this.wheelHandler);
            addEventListener('pointermove', this.onPointerMove);
            addEventListener('pointerup', this.onPointerUp);
        } else {
            this.scrollArea.eventMode = 'none';
            this.prevContainer.eventMode = 'none';
            this.indicatorScroll.eventMode = 'none';
            this.pointerScrollArea.eventMode = 'none';

            this.indicatorScroll.cursor = 'inherit';
            removeEventListener('wheel', this.wheelHandler);
            removeEventListener('pointermove', this.onPointerMove);
            removeEventListener('pointerup', this.onPointerUp);
        }
    }

    wheelHandler(e: WheelEvent) {
        e.preventDefault();

        this.smoothScrollActive = false;
        this.scrollHandler(e.deltaY);
    }
    getMaxScroll() {
        let scrollMargin = 10;
        switch (Layout.getDisplay()) {
            case Displays.MOBILE:
                scrollMargin = 45;
                break;
            case Displays.TABLET:
                scrollMargin = 28;
                break;
            case Displays.DESKTOP:
                scrollMargin = 33;
                break;
        }
        this.maxScrollHeight = Math.max(0, this.prevContainer.height - this.mask.height + scrollMargin);
        this.maxScrollbarHeight = Math.max(0, this.mainScroll.height - this.indicatorScroll.height);
    }
    scrollHandler(deltaY: number) {
        this.getMaxScroll();
        if (this.smoothScrollActive) deltaY *= 1.7;

        this.scrollbarValue += (deltaY * this.maxScrollbarHeight) / this.maxScrollHeight;
        this.scrollValue += deltaY;

        if (this.scrollValue < 0) {
            this.scrollValue = 0;
            this.scrollbarValue = this.mainScroll.position.y;
        }
        if (this.scrollValue > this.maxScrollHeight) {
            this.scrollValue = this.maxScrollHeight;
            this.scrollbarValue = this.mainScroll.position.y + this.maxScrollbarHeight;
        }
    }
    scrollHandlerIndicator(pos: number) {
        this.getMaxScroll();

        const { height } = getScreenSize();
        const prcScroll = (pos - 160) / (height - 240);

        if (prcScroll < 0) {
            this.scrollValue = 0;
            this.scrollbarValue = this.mainScroll.position.y;
        } else if (prcScroll > 1) {
            this.scrollValue = this.maxScrollHeight;
            this.scrollbarValue = this.mainScroll.position.y + this.maxScrollbarHeight;
        } else {
            this.scrollValue = lerp(0, this.maxScrollHeight, prcScroll);
            this.scrollbarValue = lerp(
                this.mainScroll.position.y,
                this.mainScroll.position.y + this.maxScrollbarHeight,
                prcScroll
            );
        }

        // this.scrollbarValue -= (deltaY * this.maxScrollbarHeight) / this.maxScrollHeight;
        // this.scrollValue -= deltaY;

        // if (this.scrollValue < 0) {
        //     this.scrollValue = 0;
        //     this.scrollbarValue = this.mainScroll.position.y;
        // }
        // if (this.scrollValue > this.maxScrollHeight) {
        //     this.scrollValue = this.maxScrollHeight;
        //     this.scrollbarValue = this.mainScroll.position.y + this.maxScrollbarHeight;
        // }
    }

    update(): void {
        if (this.smoothScrollActive) {
            this.prevContainer.position.y = lerp(
                this.prevContainer.position.y,
                -this.scrollValue,
                Time.deltaTime * 0.12
            );
            this.indicatorScroll.position.y = lerp(
                this.indicatorScroll.position.y,
                this.scrollbarValue,
                Time.deltaTime * 0.12
            );
        } else {
            this.prevContainer.position.y = -this.scrollValue;
            this.indicatorScroll.position.y = this.scrollbarValue;
        }
        this.indicatorScroll.position.x = this.mainScroll.position.x;
    }
    resize(): void {
        this.drawMask();
        this.drawScrollArea();
        this.mainScroll.clear();
        this.mainScroll.roundRect(DESKTOP_UI_WIDTH * 0.5 - 24, 30, 4, getScreenSize().height - 162, 2).fill('#FFFFFF');

        switch (Layout.getDisplay()) {
            case Displays.MOBILE:
                this.prevContainer.position.set(0, 42);
                this.mainScroll.position.set(-5, 90);
                break;
            case Displays.TABLET:
                this.prevContainer.position.set(0, 0);
                this.mainScroll.position.set(-3, 107);
                break;
            case Displays.DESKTOP:
                this.prevContainer.position.set(0, 0);
                this.mainScroll.position.set(-3, 102);
                break;
        }
    }
}
